// Pre-cutover view model, retained for history-off Threads during the rollout.
import type {Conversation} from '@stripe-internal/conversations-client';

import type {AppError} from '../../core/types';
import type {
  ControllerError,
  ConversationOverview,
  CreateConversationParams,
} from '../conversations/types';
import type {LegacyConversationsController} from '../app/types';
import {ThreadsViewModel} from './ThreadsViewModel';

export const NEW_CONVERSATION_ID = 'new-conversation';

export type ConversationViewModelState = {
  id: string;
  conversation: Conversation | null;
  overview: ConversationOverview | null;
  loading: boolean;
  loadResult: 'success' | 'error' | null;
  error: AppError | ControllerError | null;
};

type ConversationViewModelOptions = {
  id?: string;
  conversations: LegacyConversationsController;
};

const unknownError = (error: unknown): ControllerError => ({
  type: 'error',
  error: error instanceof Error ? error.message : 'Unknown conversation error',
});

const shouldConnectConversation = (
  overview?: ConversationOverview | null,
): boolean =>
  overview == null ||
  overview.is_active ||
  overview.live_chat?.status === 'active' ||
  overview.request_a_call?.status === 'queued';

// Fetching here is only for active channels that support live message catch-up.
// RAC/email transcripts are seeded when the controller reconstructs the
// conversation, and ended transcripts are static. Fetching those cached objects
// can also route through client channel state reset by post-transition cleanup,
// surfacing a conversation-exception error screen.
export const canFetchConversation = (conversation: Conversation): boolean => {
  if (conversation.hasEnded) {
    return false;
  }
  const {channel} = conversation.context;
  return channel == null || channel === 'bot' || channel === 'chat';
};

export const isConversationOverviewEnded = (
  overview?: ConversationOverview | null,
): boolean =>
  overview != null &&
  !overview.is_active &&
  overview.live_chat?.status !== 'active' &&
  overview.request_a_call?.status !== 'queued';

export class ConversationViewModel extends ThreadsViewModel<ConversationViewModelState> {
  private _conversations: LegacyConversationsController;

  private _loadingId: string | null = null;

  private _conversationCache = new Map<string, WeakRef<Conversation>>();

  private _overviewCache = new Map<string, WeakRef<ConversationOverview>>();

  constructor({
    id = NEW_CONVERSATION_ID,
    conversations,
  }: ConversationViewModelOptions) {
    super({
      id,
      conversation: null,
      overview: null,
      loading: false,
      loadResult: null,
      error: null,
    });

    this._conversations = conversations;
  }

  get conversation(): Conversation | null {
    return this.state.conversation;
  }

  get isNewConversation(): boolean {
    return this.state.id === NEW_CONVERSATION_ID;
  }

  get isEndedConversation(): boolean {
    return isConversationOverviewEnded(this.state.overview);
  }

  // Marks the view model as loading before `resolveActive` (e.g. a history
  // lookup) settles, so callers that need to decide which conversation to
  // show don't leave a gap where `loading` is still false and no conversation
  // has been chosen yet.
  resumeOrCreate = async (
    resolveActive: () => Promise<ConversationOverview | undefined>,
    createParams?: CreateConversationParams,
  ): Promise<void> => {
    this.setState({loading: true, loadResult: null, error: null});

    let active: ConversationOverview | undefined;
    try {
      active = await resolveActive();
    } catch (error) {
      this.setState({
        loading: false,
        loadResult: 'error',
        error: unknownError(error),
      });
      return;
    }

    if (active) {
      this.seedOverview(active);
      await this.load(active.id);
    } else {
      await this.createNewConversation(createParams);
    }
  };

  setError(error: AppError | ControllerError) {
    this.setState({error});
  }

  clearError() {
    this.setState({error: null});
  }

  load = async (requestedId?: string) => {
    const id = requestedId ?? this.state.id;

    if (this._loadingId === id) {
      // we're already loading this id
      return;
    }

    if (id === NEW_CONVERSATION_ID) {
      this.cacheConversation(this.state.conversation);
      this.cacheOverview(this.state.overview);
      this.state.conversation?.disconnect();
      this.setState({
        id,
        conversation: null,
        overview: null,
        loading: false,
        loadResult: null,
        error: null,
      });
      return;
    }

    const previousConversation = this.state.conversation;
    const previousOverview = this.state.overview;
    this.cacheConversation(previousConversation);
    this.cacheOverview(previousOverview);
    const cachedConversation = this.getCachedConversation(id);
    const cachedOverview = this.getCachedOverview(id);
    const canUseCachedConversation = cachedConversation != null;

    if (previousConversation && previousConversation !== cachedConversation) {
      previousConversation.disconnect();
    }

    this.setState({
      id,
      conversation: canUseCachedConversation ? cachedConversation : null,
      overview: cachedOverview,
      loading: true,
      loadResult: null,
      error: null,
    });
    this._loadingId = id;

    if (
      canUseCachedConversation &&
      cachedConversation &&
      shouldConnectConversation(cachedOverview)
    ) {
      cachedConversation.connect({
        fetch: canFetchConversation(cachedConversation),
      });
    }

    try {
      const response = await this._conversations.load(id);
      if (id !== this._loadingId) {
        return;
      }

      if (response.type === 'error') {
        this.setState({
          ...(canUseCachedConversation
            ? {}
            : {conversation: null, overview: null, error: response}),
          loading: false,
          loadResult: 'error',
        });
        this._loadingId = null;
        return;
      }

      const {conversation, overview} = response.data;
      if (conversation !== cachedConversation) {
        cachedConversation?.disconnect();
        if (shouldConnectConversation(overview)) {
          conversation.connect({fetch: false});
        }
      } else if (!shouldConnectConversation(overview)) {
        conversation.disconnect();
      }
      this.cacheConversation(conversation);
      this.cacheOverview(overview);

      this.setState({
        id: conversation.id,
        conversation,
        overview,
        loading: false,
        loadResult: 'success',
        error: null,
      });
      this._loadingId = null;
    } catch (error) {
      if (id !== this._loadingId) {
        return;
      }

      this.setState({
        ...(canUseCachedConversation
          ? {}
          : {
              conversation: null,
              overview: null,
              error: unknownError(error),
            }),
        loading: false,
        loadResult: 'error',
      });
      this._loadingId = null;
    }
  };

  createNewConversation = async (params?: CreateConversationParams) => {
    const id = NEW_CONVERSATION_ID;
    if (this._loadingId === id) {
      // we're already loading this id
      return;
    }

    this.cacheConversation(this.state.conversation);
    this.cacheOverview(this.state.overview);
    this.state.conversation?.disconnect();

    this._loadingId = id;

    this.setState({
      id,
      conversation: null,
      overview: null,
      loading: true,
      loadResult: null,
      error: null,
    });

    try {
      const response = await this._conversations.createAndLoad({
        ...params,
      });
      if (id !== this._loadingId) {
        return response;
      }

      if (response.type === 'error') {
        this.setState({
          loading: false,
          loadResult: 'error',
          error: response,
        });
        this._loadingId = null;

        return response;
      }

      const {conversation, overview} = response.data;
      if (shouldConnectConversation(overview)) {
        conversation.connect({fetch: false});
      }
      this.cacheConversation(conversation);
      this.cacheOverview(overview);

      this.setState({
        id: conversation.id,
        conversation,
        overview,
        loading: false,
        loadResult: 'success',
        error: null,
      });
      this._loadingId = null;

      return response;
    } catch (error) {
      const controllerError = unknownError(error);
      if (id === this._loadingId) {
        this.setState({
          loading: false,
          loadResult: 'error',
          error: controllerError,
        });
        this._loadingId = null;
      }

      return controllerError;
    }
  };

  patchOverview(patch: Partial<ConversationOverview>): void {
    const {overview} = this.state;
    if (!overview) return;
    if (patch.id !== undefined && patch.id !== overview.id) return;
    const merged = {...overview, ...patch};
    const changed = (
      Object.keys(merged) as (keyof ConversationOverview)[]
    ).some((k) => merged[k] !== overview[k]);
    if (!changed) return;
    this.setState({overview: merged});
  }

  seedOverview(overview: ConversationOverview): void {
    this.cacheOverview(overview);
  }

  private getCachedConversation(id: string): Conversation | null {
    const conversation = this._conversationCache.get(id)?.deref();
    if (!conversation) {
      this._conversationCache.delete(id);
    }

    return conversation ?? null;
  }

  private cacheConversation(conversation?: Conversation | null): void {
    if (conversation) {
      this._conversationCache.set(conversation.id, new WeakRef(conversation));
    }
  }

  private getCachedOverview(id: string): ConversationOverview | null {
    const overview = this._overviewCache.get(id)?.deref();
    if (!overview) {
      this._overviewCache.delete(id);
    }

    return overview ?? null;
  }

  private cacheOverview(overview?: ConversationOverview | null): void {
    if (overview) {
      this._overviewCache.set(overview.id, new WeakRef(overview));
    }
  }
}
